import React, { useState, useEffect } from 'react';
import { imageCacheService } from '../utils/imageCache';
import { BiTrash, BiRefresh } from 'react-icons/bi';
import AppHeaderLayout from '../components/AppHeaderLayout';
import { useTranslation } from 'react-i18next';
import { iconButtonStyles } from '../styles/commonStyles';

function CacheManagement() {
    const { t } = useTranslation();
    const [cacheStats, setCacheStats] = useState({
        imagesCount: 0,
        totalSize: 0,
        sizeFormatted: '0 B',
        images: { count: 0, size: 0, sizeFormatted: '0 B' },
        videos: { count: 0, size: 0, sizeFormatted: '0 B' }
    });
    const [isClearing, setIsClearing] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        loadCacheStats();
    }, []);

    const loadCacheStats = async () => {
        setIsRefreshing(true);
        try {
            const stats = await imageCacheService.getCacheStats();
            setCacheStats(stats);
        } catch (error) {
            console.error('Ошибка загрузки статистики кеша:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const clearCache = async () => {
        if (!window.confirm('Вы уверены, что хотите очистить весь кеш изображений?')) {
            return;
        }

        setIsClearing(true);
        try {
            await imageCacheService.clearCache();
            await loadCacheStats();
            alert('Кеш изображений успешно очищен!');
        } catch (error) {
            console.error('Ошибка очистки кеша:', error);
            alert('Ошибка при очистке кеша');
        } finally {
            setIsClearing(false);
        }
    };

    const leftContent = (
        <div className="flex items-center gap-2">
            <span className="font-semibold text-base">Управление кешем</span>
        </div>
    );

    const rightContent = (
        <div className="flex items-center gap-2">
            <button 
                className={iconButtonStyles} 
                onClick={loadCacheStats}
                disabled={isRefreshing}
                aria-label="Обновить статистику"
                title="Обновить статистику"
            >
                <BiRefresh className={`text-xl ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
        </div>
    );

    return (
        <AppHeaderLayout
            leftContent={leftContent}
            rightContent={rightContent}
            title="Управление кешем"
        >
            <div className="p-4">
                <div className="max-w-md mx-auto">
                    {/* Stats */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Статистика кеша
                        </h3>
                        
                        <div className="space-y-4">
                            {/* Общая статистика */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-600 dark:text-gray-400">Всего файлов:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        {cacheStats.imagesCount}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600 dark:text-gray-400">Общий размер:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        {cacheStats.sizeFormatted}
                                    </span>
                                </div>
                            </div>

                            {/* Детальная статистика по типам */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    По типам медиа:
                                </h4>
                                
                                {/* Изображения */}
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600 dark:text-gray-400">📸 Изображения:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        {cacheStats.images.count} ({cacheStats.images.sizeFormatted})
                                    </span>
                                </div>
                                
                                {/* Видео */}
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600 dark:text-gray-400">🎥 Видео:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        {cacheStats.videos.count} ({cacheStats.videos.sizeFormatted})
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                        <button
                            onClick={loadCacheStats}
                            disabled={isRefreshing}
                            className="w-full flex items-center justify-center gap-2 bg-blue-400/80 hover:bg-blue-400 disabled:bg-gray-400 text-white py-3 px-4 rounded-lg transition-colors"
                        >
                            <BiRefresh className={`text-lg ${isRefreshing ? 'animate-spin' : ''}`} />
                            {isRefreshing ? 'Обновление...' : 'Обновить статистику'}
                        </button>
                        
                        <button
                            onClick={clearCache}
                            disabled={isClearing || cacheStats.imagesCount === 0}
                            className="w-full flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white py-3 px-4 rounded-lg transition-colors"
                        >
                            <BiTrash className="text-lg" />
                            {isClearing ? 'Очистка...' : 'Очистить кеш'}
                        </button>
                    </div>

                    {/* Info */}
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                            О кеше изображений
                        </h4>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            Кеш изображений позволяет просматривать галерею без интернета. 
                            Изображения автоматически сохраняются при просмотре.
                        </p>
                    </div>
                </div>
            </div>
        </AppHeaderLayout>
    );
}

export default CacheManagement;
